import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal } from 'common/components/Modal';
import { Input } from 'common/components/Input';
import { Select } from 'common/components/Select';
import { updateUser } from 'services/api/AccessManagerAPI';
import { UserRole } from 'types/userRoles';
import { EditUserModalProps, EditUserForm, PasswordValidation } from './EditUserModal.types';
import styles from './EditUserModal.module.scss';

export const EditUserModal: React.FC<EditUserModalProps> = ({
    isOpen,
    onClose,
    user,
    onSuccess,
    onError,
    hasTenantAccess,
    selectedTenant,
}) => {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<EditUserForm>({
        defaultValues: {
            firstName: '',
            lastName: '',
            password: '',
            roles: [],
            tenantName: '',
        },
    });

    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasDigit: false,
        hasSpecialChar: false,
    });

    const password = watch('password');

    // Update form when user changes
    useEffect(() => {
        if (user) {
            reset({
                firstName: user.firstName,
                lastName: user.lastName,
                password: '',
                roles: user.roles,
                tenantName: selectedTenant?.name || '',
            });

            // Reset password validation
            setPasswordValidation({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasDigit: false,
                hasSpecialChar: false,
            });
        }
    }, [user, reset]);

    // Validate password in real-time
    useEffect(() => {
        if (password) {
            setPasswordValidation({
                minLength: password.length >= 10,
                hasUpperCase: /[A-Z]/.test(password),
                hasLowerCase: /[a-z]/.test(password),
                hasDigit: /\d/.test(password),
                hasSpecialChar: /[!@#$%^&*()_+\-=[\]{}();':"\\|,.<>/?]/.test(password),
            });
        } else {
            setPasswordValidation({
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasDigit: false,
                hasSpecialChar: false,
            });
        }
    }, [password]);

    const onSubmit = async (data: EditUserForm) => {
        if (!user) return;

        // If password is provided, validate it meets requirements
        if (data.password) {
            if (
                !passwordValidation.minLength ||
                !passwordValidation.hasUpperCase ||
                !passwordValidation.hasLowerCase ||
                !passwordValidation.hasDigit ||
                !passwordValidation.hasSpecialChar
            ) {
                onError('Password does not meet the requirements');
                return;
            }
        }

        // Create updated user object for API (convert to snake_case)
        const updatedUserData: any = {
            first_name: data.firstName,
            last_name: data.lastName,
            email: user.email, // Email is not editable, use original
            roles: user.roles, // Will be updated below based on role logic
            tenant_id: user.tenantId,
        };

        if (user.roles.includes(UserRole.SUPER_ADMIN)) {
            updatedUserData.roles = user.roles;
        } else {
            updatedUserData.roles = data.roles;
        }

        // Add password only if it was provided
        if (data.password) {
            updatedUserData.password = data.password;
        }

        try {
            // Call the API to update the user
            await updateUser(user.id, updatedUserData);

            // Map the updated user data back to camelCase
            const mappedUpdatedUser = {
                ...user, // Keep all existing properties
                firstName: data.firstName,
                lastName: data.lastName,
                email: user.email, // Email remains unchanged
                roles: updatedUserData.roles,
            };

            onSuccess(mappedUpdatedUser);
            onClose();
        } catch (error: any) {
            console.error('Error updating user:', error);
            onError(error.response?.data?.message || 'Failed to update user. Please try again.');
        }
    };

    if (!user) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title='Edit User'
            size='md'
            onDone={handleSubmit(onSubmit)}
            onCancel={onClose}
            doneText='Update User'
            cancelText='Cancel'
        >
            <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-md'>
                <Input label='Email' type='email' value={user.email} disabled readOnly />

                <Input
                    label='First Name'
                    type='text'
                    {...register('firstName', { required: 'First name is required' })}
                    error={!!errors.firstName}
                    errorText={errors.firstName?.message}
                />

                <Input
                    label='Last Name'
                    type='text'
                    {...register('lastName', { required: 'Last name is required' })}
                    error={!!errors.lastName}
                    errorText={errors.lastName?.message}
                />

                <Input
                    label='Password (leave blank to keep unchanged)'
                    type='password'
                    showPasswordToggle
                    {...register('password')}
                    error={!!errors.password}
                    errorText={errors.password?.message}
                />

                {password && (
                    <div className={styles.passwordRequirements}>
                        <p>Password requirements:</p>
                        <ul>
                            <li className={passwordValidation.minLength ? styles.valid : ''}>
                                {passwordValidation.minLength ? '✓' : '○'} At least 10 characters
                            </li>
                            <li className={passwordValidation.hasUpperCase ? styles.valid : ''}>
                                {passwordValidation.hasUpperCase ? '✓' : '○'} At least one uppercase letter
                            </li>
                            <li className={passwordValidation.hasLowerCase ? styles.valid : ''}>
                                {passwordValidation.hasLowerCase ? '✓' : '○'} At least one lowercase letter
                            </li>
                            <li className={passwordValidation.hasDigit ? styles.valid : ''}>
                                {passwordValidation.hasDigit ? '✓' : '○'} At least one digit
                            </li>
                            <li className={passwordValidation.hasSpecialChar ? styles.valid : ''}>
                                {passwordValidation.hasSpecialChar ? '✓' : '○'} At least one special character
                            </li>
                        </ul>
                    </div>
                )}

                {hasTenantAccess && selectedTenant && (
                    <Controller
                        name='tenantName'
                        control={control}
                        render={({ field }) => (
                            <Select
                                label='Tenant'
                                disabled
                                value={field.value}
                                onChange={field.onChange}
                                options={[{ value: selectedTenant.name, label: selectedTenant.name }]}
                            />
                        )}
                    />
                )}

                <Controller
                    name='roles'
                    control={control}
                    rules={{ validate: (v) => v.length > 0 || 'At least one role is required' }}
                    render={({ field }) => (
                        <Select
                            label='Roles'
                            isMulti
                            value={field.value}
                            onChange={field.onChange}
                            disabled={user.roles.includes(UserRole.SUPER_ADMIN)}
                            error={!!errors.roles}
                            errorText={errors.roles?.message}
                            placeholder='Select roles...'
                            helperText={
                                user.roles.includes(UserRole.SUPER_ADMIN)
                                    ? 'Roles cannot be changed for Super Admins'
                                    : undefined
                            }
                            options={[
                                { value: UserRole.AGENT, label: 'Agent' },
                                { value: UserRole.MANAGER, label: 'Manager' },
                                { value: UserRole.ADMIN, label: 'Admin' },
                                ...(user.roles.includes(UserRole.SUPER_ADMIN)
                                    ? [{ value: UserRole.SUPER_ADMIN, label: 'Super Admin' }]
                                    : []),
                            ]}
                        />
                    )}
                />
            </form>
        </Modal>
    );
};
